import {
  AppBar,
  Box,
  Toolbar,
  Autocomplete,
  TextField,
  CircularProgress,
  Divider,
  Typography,
  Avatar,
  ListItemIcon,
  Snackbar, 
  Alert
} from "@mui/material";
import NotificationsBell from "../components/topbar/NotificationsBell";
import AppsIcon from "@mui/icons-material/Apps";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import { useState } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAuth } from "../context/AuthContext";
import UserRoleChip from "../components/common/UserRoleChip";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useAppTranslation } from "../hooks/useAppTranslation";
import SyncIcon from "@mui/icons-material/Sync";
import { syncCompanies } from "../api/documents";

interface CompanyOption {
  id: string;
  name: string;
}

interface TopBarProps {
  drawerWidth: number;

  companies: any[];
  selectedCompany: string;
  onSelectCompany: (id: string) => void;

  onLogout: () => void;

  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;

  onCompaniesRefresh: () => Promise<void>;
}

export default function TopBar({
  drawerWidth,
  companies,
  selectedCompany,
  onSelectCompany,
  onLogout,
  onToggleSidebar,
  sidebarCollapsed,
  onCompaniesRefresh,
}: TopBarProps) {
  const { t } = useAppTranslation("common");

  const { user } = useAuth();
  const canUseAppSwitcher = user?.role !== "external";

  const isLoadingCompanies = companies === undefined;

  const options: CompanyOption[] = (companies || []).map((c: any) => ({
    id: String(c.systemId ?? c.id ?? ""),
    name: String(c.name ?? c.displayName ?? "Firma"),
  }));

  const selectedOption =
    options.find((o) => o.id === selectedCompany) ?? null;

  const handleCompanyChange = (option: CompanyOption | null) => {
    const newCompanyId = option?.id ?? "";
    onSelectCompany(newCompanyId);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: `calc(100% - ${drawerWidth}px)`,
        ml: `${drawerWidth}px`,
        transition: "all 0.25s ease",
      }}
    >
      <Toolbar sx={{ px: 2.5 }}>
        {/* ☰ SIDEBAR TOGGLE */}
        <IconButton
          color="inherit"
          onClick={onToggleSidebar}
          sx={{
            mr: 1.5,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 2,
          }}
          aria-label="Toggle sidebar"
        >
          {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        {/* 🔔 POWIADOMIENIA */}
        <NotificationsBell companyId={selectedCompany} />

        {/* 🧩 APP SWITCHER */}
        {canUseAppSwitcher && <AppSwitcher />}

        {/* 🏢 FIRMA */}
        <Box sx={{ width: 260, ml: 2 }}>
          {isLoadingCompanies ? (
            <CircularProgress size={20} />
          ) : (
            <Autocomplete
              size="small"
              options={options}
              value={selectedOption}
              onChange={(_, value) => handleCompanyChange(value)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => opt.name}
              disabled={options.length === 0}
              renderInput={(params) => (
                <TextField {...params} label={t("Firma")} size="small" />
              )}
            />
          )}
        </Box>

        {/* 👤 USER MENU */}
        <UserMenu
          onLogout={onLogout}
          onCompaniesRefresh={onCompaniesRefresh}
        />
      </Toolbar>
    </AppBar>
  );
}

function AppSwitcher() {
  const { t } = useAppTranslation("common");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ ml: 1 }}
      >
        <AppsIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, opacity: 0.6 }}
        >
          {t("Aplikacje")}
        </Typography>

        <Divider />

        <MenuItem
          component="a"
          href="https://businesscentral.dynamics.com/5373eaba-0c25-4568-9278-fdeff3e9e96e/TestNewPLF?company=EKO%20Portal"
          target="_blank"
        >
          Business Central
        </MenuItem>

        <MenuItem component="a" href="#" target="_blank">
          CRM Sales
        </MenuItem>
      </Menu>
    </>
  );
}

function UserMenu({
  onLogout,
  onCompaniesRefresh,
}: {
  onLogout: () => void;
  onCompaniesRefresh: () => Promise<void>;
}) {
  const { t } = useAppTranslation("common");
  const { user } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const open = Boolean(anchorEl);

  const initials = user?.display_name
    ? user.display_name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  const handleSync = async () => {
    try {
      setLoading(true);

      const result = await syncCompanies();

      // 🔄 odświeżenie listy firm
      await onCompaniesRefresh();

      setSnackbar({
        message: `Utworzono: ${result.created}, Zaktualizowano: ${result.updated}`,
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        message: error?.message || "Błąd synchronizacji firm",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setAnchorEl(null);
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ ml: 1 }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 14,
            bgcolor: "primary.main",
          }}
        >
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => !loading && setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 2,
            mt: 1,
          },
        }}
      >
        {/* USER INFO */}
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <Typography
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontWeight: 600,
              opacity: 0.6,
            }}
          >
            {t("Zalogowany jako")}:
          </Typography>

          <Typography fontWeight={600} sx={{ mt: 0.5 }}>
            {user?.display_name || t("Użytkownik")}
          </Typography>

          <Box sx={{ mt: 1 }}>
            <UserRoleChip role={user?.role || "external"} />
          </Box>

          <Typography
            variant="caption"
            sx={{
              mt: 2,
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontWeight: 600,
              opacity: 0.6,
            }}
          >
            Email
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {user?.email}
          </Typography>
        </Box>

        <Divider />

        {/* ADMIN ACTION */}
        {user?.role === "admin" && (
          <MenuItem
            onClick={handleSync}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <SyncIcon fontSize="small" sx={{ color: "#1c6e8c" }} />
              )}
            </ListItemIcon>
            {loading ? t("Synchronizacja...") : t("Synchronizuj firmy z BC")}
          </MenuItem>
        )}

        {user?.role === "admin" && <Divider />}

        {/* LOGOUT */}
        <MenuItem
          onClick={onLogout}
          disabled={loading}
          sx={{
            py: 1.5,
            color: "error.main",
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          {t("Wyloguj się")}
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}